import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Container from "@mui/material/Container";
import PropTypes from "prop-types";
import { useLocation, useParams } from "react-router-dom";
import { SFStack } from "../grid";
import getStyled from "../../hooks/getStyled";
import {
  setFullScreen,
  setActionBarSticky,
} from "../../redux/reducers/pageLayoutReducer";
import PageErrorBoundary from "./pageErrorBoundary";
import {
  Wave1,
  Wave2,
  Wave3,
  Wave4,
  Wave5,
  Wave6,
} from "../../assets/icons/backgroundWaves";
import { getWaveImage } from "./helpers";

const { styled } = getStyled();

// Wave background images pool
const waves = [Wave1, Wave2, Wave3, Wave4, Wave5, Wave6];

//this is the root of the action bar
const ActionBarRoot = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "top" &&
    prop !== "fullScreenMode" &&
    prop !== "hasChildren" &&
    prop !== "hasSideComponent",
})(({ theme, top, fullScreenMode, hasChildren, hasSideComponent }) => ({
  position: "sticky",
  borderBottom: hasChildren
    ? `1px solid ${theme.palette.grey200.main}`
    : "none",
  //top is been calculated based on the height of the header
  top: fullScreenMode ? 0 : top - 1,
  zIndex: 10,
  ...(fullScreenMode && {
    backgroundColor: theme.palette.white.main,
  }),
  //this class will be added when the action bar is sticked to the header
  "&.stick-to-header": {
    backgroundColor: theme.palette.white.main,
    //theme.spacing(2.5) is the padding of the container and wrapper of action bar
    //so in case the action bar get's sticky, we need to add the negative paddings in order to remove the paddings from the container
    marginLeft: theme.spacing(-2.5),
    paddingLeft: theme.spacing(2.5),
    ...(!hasSideComponent && {
      paddingRight: theme.spacing(2.5),
      marginRight: theme.spacing(-2.5),
    }),
  },
}));

const SideComponentWrapper = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "top" && prop !== "fullScreenMode" && prop !== "open",
})(({ theme, top, fullScreenMode, open }) => ({
  position: "sticky",
  top: fullScreenMode ? 0 : top - 3,
  alignSelf: "flex-start",
  display: open ? "block" : "none",
}));

const SFContainer = (props) => {
  return <Container {...props}>{props.children}</Container>;
};

SFContainer.defaultProps = {
  maxWidth: false,
  disableGutters: false,
};

SFContainer.propTypes = {
  //The content of the component.
  children: PropTypes.node,
  // Override or extend the styles applied to the component. See CSS API below for more details.
  classes: PropTypes.any,
  // The component used for the root node. Either a string to use a HTML element or a component.
  component: PropTypes.elementType,
  // If true, the left and right padding is removed.
  disableGutters: PropTypes.bool,
  // Set the max-width to match the min-width of the current breakpoint. This is useful if
  // you'd prefer to design for a fixed set of sizes instead of trying to accommodate a fully fluid
  // viewport. It's fluid by default.
  fixed: PropTypes.bool,
  //     'xs'
  // | 'sm'
  // | 'md'
  // | 'lg'
  // | 'xl'
  // | false
  // | string
  // Determine the max-width of the container. The container width grows with the size of the screen.
  // Set to false to disable maxWidth.
  maxWidth: PropTypes.any,
  // Array<func
  // | object
  // | bool>
  // | func
  // | object
  // The system prop that allows defining system overrides as well as additional CSS styles. See the
  // `sx` page for more details.
  sx: PropTypes.any,
};

const getBgColor = (palette, bgColorVariant) => {
  let color;
  switch (bgColorVariant) {
  case "primary":
  default:
    color = palette.sf_bg_gradient.main;
    break;
  case "secondary":
    color = palette.white.main;
    break;
  }
  return color;
};

export const SFPageContainer = (props) => {
  const location = useLocation();
  const params = useParams();
  const [top, setTop] = useState(0);
  //this is used as a counter to force the action bar to re-render when the children of the action bar are changed (counter increases)
  const [actionBarChanged, setActionBarChanged] = useState(0);
  const dispatch = useDispatch();
  const isActionBarSticky = useSelector(
    (state) => state.pageLayout.actionBar.sticky
  );
  const pageError = useSelector((state) => state.session.pageError);

  const fullScreenMode = useSelector((state) => state.pageLayout.fullScreen);
  let actionBarRootRef = useRef(null);

  const { disableGutters, bgColorVariant, removeBgWave, className, ...other } = props;

  useEffect(() => {
    //every time a new page is rendered we want to make sure that we exit the full screen mode
    dispatch(setFullScreen(false));
  }, []);

  useEffect(() => {
    // Ensure the ref is current and not null
    if (actionBarRootRef.current) {
      // Create a new MutationObserver instance
      const observer = new MutationObserver((mutations) => {
        setActionBarChanged((curState) => curState + 1);
      });

      // Start observing the target node for configured mutations
      observer.observe(actionBarRootRef.current, {
        childList: true, // Set to true to observe changes to the children of the node
        // You can add other configuration options if needed
      });

      // Clean up the observer on component unmount
      return () => {
        observer.disconnect();
      };
    }
  }, []); // Empty dependency array ensures this effect runs only once

  useEffect(() => {
    if (fullScreenMode) {
      return;
    }
    let appBarEl = document.getElementById("appBar");
    //creating an observer and observing the appBar element size changes (height)
    const resizeObserver = new ResizeObserver(() => {
      let actionBarHasChildren =
        actionBarRootRef.current?.childNodes?.length > 0;
      //if action bar has children, set the top of the action bar to the height of the appBar
      //in order to stick just below the appBar
      if (actionBarHasChildren) {
        setTop(appBarEl.offsetHeight);
      }
    });
    resizeObserver.observe(appBarEl);
    // clean up
    return () => {
      resizeObserver.disconnect();
    };
  }, [fullScreenMode, actionBarChanged]);

  useEffect(() => {
    if (pageError) {
      return;
    }
    //if full screen is enabled we want to unstick the action bar from the header (if it was sticked before)
    if (fullScreenMode) {
      dispatch(setActionBarSticky(false));
      return;
    }
    let actionBarEl = actionBarRootRef.current;
    //creating an observer and observing the intersection of the action bar with the browser viewport
    //rootMargin will add an extra space to the browser viewport (works as margin - currently top margin is added to the rootMargin)
    //adding an extra 1px so there's an extra space between the action bar and the appBar before they start to intersect
    //the intersectionRatio will be 1 when the action bar is not intersecting with the browser viewport and below 1 when it starts to intersect
    const observer = new IntersectionObserver(
      ([e]) => {
        dispatch(
          setActionBarSticky(
            e.intersectionRatio < 1 && actionBarEl?.childNodes?.length > 0
          )
        );
      },
      {
        threshold: [1],
        rootMargin: `-${top + 1}px 0px 0px 0px`,
      }
    );
    observer.observe(actionBarEl);
    // clean up
    return () => {
      observer.disconnect();
    };
  }, [top, fullScreenMode, actionBarChanged, pageError]);

  return (
    <SFStack
      sx={(theme) => ({
        background: getBgColor(theme.palette, bgColorVariant),
        width: "100%",
        flex: 1,
        //remove padding if fullScreenMode is true
        padding: fullScreenMode ? 0 : theme.spacing(2.5),
        ...(fullScreenMode && {
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 99999,
          height: "100vh",
          overflow: "auto",
        }),
      })}
      className={className}
      direction="row"
      spacing={2.5}
    >
      <Container
        maxWidth="100%"
        disableGutters
        sx={(theme) => ({
          ...other.sx,
          backgroundColor: theme.palette.white.main,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          maxWidth: theme.screenMaxWidth,
          ...(!fullScreenMode && {
            borderRadius: "4px",
            border: `1px solid ${theme.palette.grey225.main}`,
          }),
          ...(!removeBgWave && {
            backgroundImage: `url(${getWaveImage(waves, location, params)})`,
            backgroundSize: "100% auto",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center bottom",
          }),
        })}
        {...other}
      >
        <PageErrorBoundary>
          <ActionBarRoot
            id="actionBarRoot"
            top={top}
            ref={actionBarRootRef}
            fullScreenMode={fullScreenMode}
            hasChildren={actionBarRootRef.current?.childNodes.length > 0}
            {...(isActionBarSticky && { className: "stick-to-header" })}
            hasSideComponent={props.sideComponentProps?.open}
          />
          <SFStack
            sx={(theme) => ({
              padding: disableGutters ? 0 : theme.spacing(2.5),
              flex: 1,
              display: "flex",
              flexDirection: "column",
            })}
          >
            {props.children}
          </SFStack>
        </PageErrorBoundary>
      </Container>
      {props.sideComponent && (
        <SideComponentWrapper
          top={top}
          fullScreenMode={fullScreenMode}
          open={props.sideComponentProps?.open}
        >
          {props.sideComponent}
        </SideComponentWrapper>
      )}
    </SFStack>
  );
};

SFPageContainer.defaultProps = {
  bgColorVariant: "primary",
  disableGutters: false,
  removeBgWave: false,
};

SFPageContainer.propTypes = {
  /**No padding removes extra space from the page container */
  disableGutters: PropTypes.bool,
  /**Used to define the background color the page container */
  bgColorVariant: PropTypes.oneOf(["primary", "secondary"]),
  /**Used to remove the background wave from the page container */
  removeBgWave: PropTypes.bool,
};

export default SFContainer;
